import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { mensajeDeCampo, tieneCodigo } from '../api';
import type {
  Cliente,
  EmitirRequest,
  GuardarClienteRequest,
  ItemRequest,
  ReceptorRequest,
  TenantResumen,
} from '../api';
import { guardarCliente, useFichaDelReceptor } from '../clientes';
import { nombreDeTenant } from '../tenants';
import { emitirComprobante } from './api';
import {
  ALICUOTAS,
  CONCEPTOS,
  CONDICIONES_IVA_RECEPTOR,
  TIPOS_DE_DOCUMENTO,
  requiereFicha,
  totalCargado,
} from './dominio';
import { useClaveDeIdempotencia } from './idempotencia';
import { mensajeDeEmision } from './mensajes';
import { ResultadoAprobado } from './ResultadoAprobado';
import { ResultadoRechazado } from './ResultadoRechazado';

/**
 * El formulario de emisión (KAN-145). **La pantalla más delicada del sistema
 * entero**, según la propia tarjeta: lo que sale de acá es un comprobante
 * fiscal que no se borra, se corrige con nota de crédito.
 *
 * # LAS TRES COSAS QUE ESTA PANTALLA NO HACE, A PROPÓSITO
 *
 * 1. **No elige la letra.** No hay ningún control A/B/C: sale de cruzar la
 *    condición fiscal del emisor (el tenant, que el front ni conoce) con la
 *    del receptor, y solo el backend tiene las dos. La letra se muestra en
 *    {@link ResultadoAprobado} como resultado, nunca como entrada.
 * 2. **No calcula el IVA.** Desde KAN-191 se cargan importes **finales, con
 *    IVA**, y el total del comprobante es exactamente su suma: eso sí se
 *    muestra ({@link totalCargado}). Cuánto de ese total es IVA lo despeja el
 *    servicio, y depende de la letra: sin la condición fiscal del emisor no se
 *    sabe si el comprobante discrimina IVA o es clase C.
 * 3. **No reintenta sola.** `crearQueryClient` (`../api/queryClient.ts`) ya
 *    pone `mutations.retry: false` en todo el front, y acá importa más que en
 *    cualquier otra pantalla: un reintento automático con una clave nueva
 *    sería el bug que la idempotencia existe para prevenir.
 *
 * # LO QUE ES SOLO DE IMPRESIÓN (KAN-186, 187, 173)
 *
 * La **descripción de cada ítem** y la **condición de venta** no viajan a ARCA:
 * salen en el PDF y nada más. El **nombre y el domicilio del receptor** tampoco:
 * WSFE no los lleva, así que se guardan como ficha (`PUT /api/v1/clientes`) y el
 * PDF las lee al generarse. Se guarda **antes** de emitir y solo si cambió: si la
 * ficha falla no se emite nada, y si la emisión falla la ficha queda guardada, lo
 * cual es inofensivo (es un upsert).
 *
 * Para un receptor identificado (todo salvo consumidor final) el PDF se niega a
 * generarse sin nombre y domicilio, así que **acá tampoco se deja emitir sin
 * ellos**: es preferible frenar antes de crear un comprobante fiscal que después
 * no se puede imprimir.
 *
 * # LA CLAVE DE IDEMPOTENCIA
 *
 * Ver `idempotencia.ts` para la regla completa. Acá solo se aplica: al
 * terminar la mutación se decide si {@link useClaveDeIdempotencia}.renovar()
 * corresponde, mirando el resultado — nunca se renueva "por las dudas".
 */
export function FormularioDeEmision({ tenants }: { tenants: readonly TenantResumen[] }) {
  const idBase = useId();

  const [tenantId, setTenantId] = useState<string>('');
  const [concepto, setConcepto] = useState<1 | 2 | 3>(1);
  const [docTipo, setDocTipo] = useState<ReceptorRequest['docTipo']>(80);
  const [docNro, setDocNro] = useState<string>('');
  const [condicionIva, setCondicionIva] = useState<string>('');
  const [items, setItems] = useState<Item[]>([nuevoItem()]);
  const [fechaEmision, setFechaEmision] = useState<string>('');
  const [servicioDesde, setServicioDesde] = useState<string>('');
  const [servicioHasta, setServicioHasta] = useState<string>('');
  const [vtoPago, setVtoPago] = useState<string>('');
  const [condicionVenta, setCondicionVenta] = useState<string>('');
  // Lo que la persona escribió en la ficha, atado al receptor para el que lo
  // escribió: si cambia el documento, lo tipeado para el anterior no viaja al
  // nuevo. Mientras no escribe nada, se ve la ficha ya guardada (si la hay).
  const [edicionDeFicha, setEdicionDeFicha] = useState<EdicionDeFicha | null>(null);

  const fichaBuscada = claveDeFicha(tenantId, docTipo, docNro);
  const ficha = useFichaDelReceptor(fichaBuscada?.clave);
  const idDeReceptor = fichaBuscada?.id ?? '';
  const editada =
    edicionDeFicha !== null &&
    (edicionDeFicha.receptor === idDeReceptor || edicionDeFicha.receptor === '');
  const nombre = editada ? edicionDeFicha.nombre : (ficha.data?.nombre ?? '');
  const domicilio = editada ? edicionDeFicha.domicilio : (ficha.data?.domicilio ?? '');

  function editarFicha(cambios: Partial<Pick<EdicionDeFicha, 'nombre' | 'domicilio'>>): void {
    setEdicionDeFicha({ receptor: idDeReceptor, nombre, domicilio, ...cambios });
  }

  const { clave, renovar } = useClaveDeIdempotencia();

  const emision = useMutation({
    mutationFn: async ({
      pedido,
      ficha: fichaAGuardar,
    }: {
      pedido: EmitirRequest;
      ficha: GuardarClienteRequest | null;
    }) => {
      // Primero la ficha: si falla, no se emitió nada. Es un upsert, así que un
      // reintento de la emisión con la misma clave la vuelve a mandar sin daño.
      if (fichaAGuardar !== null) {
        await guardarCliente(fichaAGuardar);
      }
      return emitirComprobante(pedido, clave);
    },
    onSuccess: () => {
      // El intento terminó en cualquiera de los dos casos: aprobado, el
      // comprobante ya existe; rechazado, el backend liberó la clave y
      // reintentar es corregir un pedido, que es otro intento. Ver
      // `idempotencia.ts` para el porqué exacto de cada caso.
      renovar();
    },
    onError: (error) => {
      // Las dos únicas situaciones donde el intento SIGUE abierto y hay que
      // conservar la clave: sin respuesta, o "ya hay una emisión en curso con
      // esta clave". Cualquier otro error (400/403/404/409-operacion_invalida/
      // 422-clave_reusada/500) exige una clave nueva para el próximo intento —
      // ver `idempotencia.ts`.
      if (mantieneLaClave(error)) {
        return;
      }
      renovar();
    },
  });

  const resultado = emision.data;

  function agregarItem(): void {
    setItems((previos) => [...previos, nuevoItem()]);
  }

  function quitarItem(id: string): void {
    setItems((previos) => (previos.length > 1 ? previos.filter((i) => i.id !== id) : previos));
  }

  function actualizarItem(
    id: string,
    cambios: Partial<Pick<Item, 'alicuota' | 'importeFinal' | 'descripcion'>>,
  ): void {
    setItems((previos) => previos.map((i) => (i.id === id ? { ...i, ...cambios } : i)));
  }

  function alEmitirOtro(): void {
    // Comprobante nuevo de verdad: formulario en blanco y otra clave, no una
    // reutilización de la que ya se cerró en `onSuccess`.
    setTenantId('');
    setConcepto(1);
    setDocTipo(80);
    setDocNro('');
    setCondicionIva('');
    setItems([nuevoItem()]);
    setFechaEmision('');
    setServicioDesde('');
    setServicioHasta('');
    setVtoPago('');
    setCondicionVenta('');
    setEdicionDeFicha(null);
    emision.reset();
  }

  const total = totalCargado(items);
  const receptorIdentificado = docTipo !== 99;
  const fichaObligatoria = requiereFicha(docTipo, condicionIva === '' ? null : Number(condicionIva));
  const puedeEmitir =
    tenantId !== '' &&
    condicionIva !== '' &&
    (docTipo === 99 || docNro !== '') &&
    (!fichaObligatoria || (nombre.trim() !== '' && domicilio.trim() !== '')) &&
    items.every((i) => i.importeFinal.trim() !== '');

  function alEnviar(evento: FormEvent): void {
    evento.preventDefault();
    if (!puedeEmitir || emision.isPending) {
      return;
    }
    const pedido: EmitirRequest = {
      tenantId: Number(tenantId),
      concepto,
      receptor: {
        docTipo,
        docNro: docTipo === 99 ? 0 : Number(docNro),
        condicionIva: Number(condicionIva) as ReceptorRequest['condicionIva'],
      },
      items: items.map((i) => ({
        alicuota: i.alicuota,
        importeFinal: i.importeFinal.trim(),
        descripcion: i.descripcion.trim() === '' ? null : i.descripcion.trim(),
      })),
      fechaEmision: fechaEmision === '' ? null : fechaEmision,
      servicioDesde: concepto === 1 || servicioDesde === '' ? null : servicioDesde,
      servicioHasta: concepto === 1 || servicioHasta === '' ? null : servicioHasta,
      vtoPago: concepto === 1 || vtoPago === '' ? null : vtoPago,
      condicionVenta: condicionVenta.trim() === '' ? null : condicionVenta.trim(),
    };
    emision.mutate({ pedido, ficha: fichaAGuardar(pedido.receptor) });
  }

  /**
   * La ficha a guardar antes de emitir, o `null` si no hace falta: consumidor
   * final anónimo, nada escrito, o nada distinto de lo que ya está guardado.
   */
  function fichaAGuardar(receptor: ReceptorRequest): GuardarClienteRequest | null {
    if (!receptorIdentificado || nombre.trim() === '' || receptor.docTipo === 99) {
      return null;
    }
    const nuevo: GuardarClienteRequest = {
      tenantId: Number(tenantId),
      docTipo: receptor.docTipo,
      docNro: receptor.docNro,
      nombre: nombre.trim(),
      condicionIva: receptor.condicionIva,
      ...(domicilio.trim() === '' ? {} : { domicilio: domicilio.trim() }),
    };
    return fichaSinCambios(ficha.data, nuevo) ? null : nuevo;
  }

  // Aprobado: la pantalla del resultado reemplaza al formulario entero — no
  // tiene sentido seguir viendo campos de un pedido que ya se resolvió.
  if (resultado?.resultado === 'APROBADO') {
    return <ResultadoAprobado comprobante={resultado} alEmitirOtro={alEmitirOtro} />;
  }

  return (
    <form onSubmit={alEnviar} noValidate>
      {resultado?.resultado === 'RECHAZADO' ? <ResultadoRechazado comprobante={resultado} /> : null}

      {emision.isError ? (
        <p role="alert" className="error">
          {mensajeDeEmision(emision.error)}
        </p>
      ) : null}

      <p>
        <label htmlFor={`${idBase}-tenant`}>Emitir a nombre de</label>
        <br />
        <select
          id={`${idBase}-tenant`}
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          aria-invalid={mensajeDeCampo(emision.error, 'tenantId') !== null}
          required
        >
          <option value="">Elegir tenant…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {nombreDeTenant(t)}
            </option>
          ))}
        </select>
        {tenants.length === 0 ? (
          <span className="nota"> No hay ningún tenant habilitado para emitir ahora mismo.</span>
        ) : null}
        {mensajeDeCampo(emision.error, 'tenantId') ? (
          <span className="error"> {mensajeDeCampo(emision.error, 'tenantId')}</span>
        ) : null}
      </p>

      <p>
        <label htmlFor={`${idBase}-concepto`}>Concepto</label>
        <br />
        <select
          id={`${idBase}-concepto`}
          value={concepto}
          onChange={(e) => setConcepto(Number(e.target.value) as 1 | 2 | 3)}
        >
          {CONCEPTOS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.etiqueta}
            </option>
          ))}
        </select>
      </p>

      <fieldset>
        <legend>Receptor</legend>
        <p>
          <label htmlFor={`${idBase}-docTipo`}>Tipo de documento</label>
          <br />
          <select
            id={`${idBase}-docTipo`}
            value={docTipo}
            onChange={(e) => {
              const nuevo = Number(e.target.value) as ReceptorRequest['docTipo'];
              setDocTipo(nuevo);
              if (nuevo === 99) {
                setDocNro('0');
              }
            }}
          >
            {TIPOS_DE_DOCUMENTO.map((t) => (
              <option key={t.id} value={t.id}>
                {t.etiqueta}
              </option>
            ))}
          </select>
        </p>
        <p>
          <label htmlFor={`${idBase}-docNro`}>Número de documento</label>
          <br />
          <input
            id={`${idBase}-docNro`}
            inputMode="numeric"
            value={docTipo === 99 ? '0' : docNro}
            onChange={(e) => setDocNro(e.target.value)}
            disabled={docTipo === 99}
            required={docTipo !== 99}
          />
          {docTipo === 99 ? (
            <span className="nota"> Consumidor final anónimo: siempre 0.</span>
          ) : null}
        </p>
        <p>
          <label htmlFor={`${idBase}-condicionIva`}>Condición frente al IVA</label>
          <br />
          <select
            id={`${idBase}-condicionIva`}
            value={condicionIva}
            onChange={(e) => setCondicionIva(e.target.value)}
            required
          >
            <option value="">Elegir condición…</option>
            {CONDICIONES_IVA_RECEPTOR.map((c) => (
              <option key={c.id} value={c.id}>
                {c.etiqueta}
              </option>
            ))}
          </select>
          <br />
          <span className="nota">
            La letra del comprobante (A, B o C) sale de esto cruzado con la condición fiscal del
            emisor. No se elige acá: la va a mostrar el resultado.
          </span>
        </p>
        {receptorIdentificado ? (
          <>
            <p>
              <label htmlFor={`${idBase}-nombre`}>
                Nombre o razón social{fichaObligatoria ? '' : ' (opcional)'}
              </label>
              <br />
              <input
                id={`${idBase}-nombre`}
                value={nombre}
                maxLength={200}
                onChange={(e) => editarFicha({ nombre: e.target.value })}
                aria-invalid={mensajeDeCampo(emision.error, 'nombre') !== null}
                required={fichaObligatoria}
              />
              {mensajeDeCampo(emision.error, 'nombre') ? (
                <span className="error"> {mensajeDeCampo(emision.error, 'nombre')}</span>
              ) : null}
            </p>
            <p>
              <label htmlFor={`${idBase}-domicilio`}>
                Domicilio{fichaObligatoria ? '' : ' (opcional)'}
              </label>
              <br />
              <input
                id={`${idBase}-domicilio`}
                value={domicilio}
                maxLength={200}
                onChange={(e) => editarFicha({ domicilio: e.target.value })}
                aria-invalid={mensajeDeCampo(emision.error, 'domicilio') !== null}
                required={fichaObligatoria}
              />
              {mensajeDeCampo(emision.error, 'domicilio') ? (
                <span className="error"> {mensajeDeCampo(emision.error, 'domicilio')}</span>
              ) : null}
              <br />
              <span className="nota">
                Se guardan como ficha del receptor y son lo que sale impreso en el PDF (RG 1415,
                Anexo II). WSFE no los lleva.
                {ficha.data && !editada ? ' Ya estaba fichado: se completó con lo guardado.' : ''}
              </span>
            </p>
          </>
        ) : null}
      </fieldset>

      {concepto !== 1 ? (
        <fieldset>
          <legend>Período facturado</legend>
          <p className="nota">Si se dejan vacíos, ARCA los completa con la fecha de emisión.</p>
          <p>
            <label htmlFor={`${idBase}-servicioDesde`}>Desde</label>{' '}
            <input
              id={`${idBase}-servicioDesde`}
              type="date"
              value={servicioDesde}
              onChange={(e) => setServicioDesde(e.target.value)}
            />
          </p>
          <p>
            <label htmlFor={`${idBase}-servicioHasta`}>Hasta</label>{' '}
            <input
              id={`${idBase}-servicioHasta`}
              type="date"
              value={servicioHasta}
              onChange={(e) => setServicioHasta(e.target.value)}
            />
          </p>
          <p>
            <label htmlFor={`${idBase}-vtoPago`}>Vencimiento de pago</label>{' '}
            <input
              id={`${idBase}-vtoPago`}
              type="date"
              value={vtoPago}
              onChange={(e) => setVtoPago(e.target.value)}
            />
          </p>
        </fieldset>
      ) : null}

      <fieldset>
        <legend>Ítems (importes FINALES, con IVA incluido)</legend>
        <table>
          <thead>
            <tr>
              <th scope="col">Descripción (opcional)</th>
              <th scope="col">Alícuota</th>
              <th scope="col">Importe final (con IVA)</th>
              <th scope="col">
                <span className="oculto-visualmente">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, indice) => (
              <FilaDeItem
                key={item.id}
                item={item}
                indice={indice}
                idBase={idBase}
                puedeQuitar={items.length > 1}
                error={emision.error}
                onCambiar={(cambios) => actualizarItem(item.id, cambios)}
                onQuitar={() => quitarItem(item.id)}
              />
            ))}
          </tbody>
        </table>
        <button type="button" onClick={agregarItem}>
          Agregar ítem
        </button>
        <p>
          Total del comprobante (<strong>con IVA</strong>):{' '}
          <strong>
            {total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </strong>
        </p>
        <p className="nota">
          Cargá lo que paga el cliente, con el IVA adentro: el comprobante sale exactamente por
          ese total. El servicio calcula cuánto es neto y cuánto IVA según la letra que resulte.
        </p>
        <p className="nota">
          La descripción es solo de impresión: no viaja a ARCA. Si ningún ítem la lleva, el PDF
          dice &quot;Productos&quot; o &quot;Servicios&quot;; si alguno la lleva, imprime una fila
          por ítem.
        </p>
      </fieldset>

      <p>
        <label htmlFor={`${idBase}-fechaEmision`}>Fecha de emisión</label>{' '}
        <input
          id={`${idBase}-fechaEmision`}
          type="date"
          value={fechaEmision}
          onChange={(e) => setFechaEmision(e.target.value)}
        />
        <br />
        <span className="nota">Vacío = hoy, en la zona horaria de Argentina.</span>
      </p>

      <p>
        <label htmlFor={`${idBase}-condicionVenta`}>Condición de venta</label>{' '}
        <input
          id={`${idBase}-condicionVenta`}
          value={condicionVenta}
          maxLength={100}
          placeholder="Contado, transferencia bancaria…"
          onChange={(e) => setCondicionVenta(e.target.value)}
        />
        <br />
        <span className="nota">Opcional. Solo se imprime en el PDF: no viaja a ARCA.</span>
      </p>

      <button type="submit" disabled={!puedeEmitir || emision.isPending}>
        {emision.isPending ? 'Emitiendo…' : 'Emitir'}
      </button>
    </form>
  );
}

interface Item {
  id: string;
  descripcion: string;
  alicuota: ItemRequest['alicuota'];
  importeFinal: string;
}

function nuevoItem(): Item {
  return { id: crypto.randomUUID(), descripcion: '', alicuota: 5, importeFinal: '' };
}

/** Lo que la persona escribió en nombre y domicilio, y para qué receptor. */
interface EdicionDeFicha {
  /** Identifica al receptor (`''` si todavía no había documento completo). */
  receptor: string;
  nombre: string;
  domicilio: string;
}

/**
 * La clave con la que buscar la ficha, o `undefined` si todavía no hay con qué:
 * sin tenant, consumidor final anónimo (que no se ficha) o un documento a medio
 * escribir (una consulta por cada tecla sería ruido).
 */
function claveDeFicha(
  tenantId: string,
  docTipo: ReceptorRequest['docTipo'],
  docNro: string,
): { id: string; clave: { tenantId: number; docTipo: Cliente['docTipo']; docNro: number } } | undefined {
  if (tenantId === '' || docTipo === 99 || !/^\d{7,11}$/.test(docNro)) {
    return undefined;
  }
  return {
    id: `${tenantId}-${docTipo}-${docNro}`,
    clave: { tenantId: Number(tenantId), docTipo, docNro: Number(docNro) },
  };
}

/** `true` si lo que se iba a guardar ya es lo que está guardado. */
function fichaSinCambios(
  existente: Cliente | null | undefined,
  nueva: GuardarClienteRequest,
): boolean {
  return (
    existente != null &&
    existente.nombre === nueva.nombre &&
    existente.condicionIva === nueva.condicionIva &&
    (existente.domicilio ?? '') === (nueva.domicilio ?? '')
  );
}

function FilaDeItem({
  item,
  indice,
  idBase,
  puedeQuitar,
  error,
  onCambiar,
  onQuitar,
}: {
  item: Item;
  indice: number;
  idBase: string;
  puedeQuitar: boolean;
  error: unknown;
  onCambiar: (cambios: Partial<Pick<Item, 'alicuota' | 'importeFinal' | 'descripcion'>>) => void;
  onQuitar: () => void;
}) {
  // El backend informa los campos de Bean Validation con el path del binding
  // (`items[0].importeFinal`); si algún día cambia el formato esto simplemente deja
  // de encontrar coincidencia y el error sigue mostrándose arriba, genérico.
  const errorDeImporte = mensajeDeCampo(error, `items[${indice}].importeFinal`);
  return (
    <tr>
      <td>
        <label className="oculto-visualmente" htmlFor={`${idBase}-descripcion-${item.id}`}>
          Descripción del ítem {indice + 1}
        </label>
        <input
          id={`${idBase}-descripcion-${item.id}`}
          value={item.descripcion}
          maxLength={200}
          onChange={(e) => onCambiar({ descripcion: e.target.value })}
        />
      </td>
      <td>
        <label className="oculto-visualmente" htmlFor={`${idBase}-alicuota-${item.id}`}>
          Alícuota del ítem {indice + 1}
        </label>
        <select
          id={`${idBase}-alicuota-${item.id}`}
          value={item.alicuota}
          onChange={(e) => onCambiar({ alicuota: Number(e.target.value) as ItemRequest['alicuota'] })}
        >
          {ALICUOTAS.map((a) => (
            <option key={a.id} value={a.id}>
              {a.etiqueta}
            </option>
          ))}
        </select>
      </td>
      <td>
        <label className="oculto-visualmente" htmlFor={`${idBase}-importe-${item.id}`}>
          Importe final con IVA del ítem {indice + 1}
        </label>
        <input
          id={`${idBase}-importe-${item.id}`}
          inputMode="decimal"
          value={item.importeFinal}
          onChange={(e) => onCambiar({ importeFinal: e.target.value })}
          aria-invalid={errorDeImporte !== null}
          required
        />
        {errorDeImporte ? <span className="error"> {errorDeImporte}</span> : null}
      </td>
      <td>
        <button type="button" onClick={onQuitar} disabled={!puedeQuitar}>
          Quitar
        </button>
      </td>
    </tr>
  );
}

/**
 * `true` si, tras este error, el intento sigue abierto y hay que mandar la
 * MISMA clave la próxima vez. Ver la lista completa y el porqué de cada caso
 * en `idempotencia.ts`.
 */
function mantieneLaClave(error: unknown): boolean {
  return tieneCodigo(error, 'sin_conexion', 'emision_en_curso');
}
